import React, { useState, useEffect } from 'react';
import { MdArrowBack, MdClose } from 'react-icons/md';
import { HiLightBulb } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { images } from '../../constants';
import activities_data from '../../data/activities.json';
import '../AllProjects/AllProjects.scss'; // Reusing the exact same grid & modal CSS

const CATEGORIES = ['ALL', 'Competitions', 'LeaderShip', 'Communities', 'Events', 'Talks'];

const AllActivities = () => {
  const [activities, setActivities] = useState([]);
  const [filterActivity, setFilterActivity] = useState([]);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [animateCard, setAnimateCard] = useState({ opacity: 1, y: 0 });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Only include activities that have an image defined
    const validActivities = activities_data.filter((a) => a.img && images[a.img]);
    setActivities(validActivities);
    setFilterActivity(validActivities);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selectedActivity ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedActivity]);

  const handleActivityFilter = (item) => {
    setActiveFilter(item);
    setAnimateCard({ opacity: 0, y: 20 });
    setTimeout(() => {
      setAnimateCard({ opacity: 1, y: 0 });
      setFilterActivity(item === 'ALL' ? activities : activities.filter((a) => a.tags.includes(item)));
    }, 400);
  };

  return (
    <div className="ap__page">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="ap__header">
        <button className="ap__back-btn" onClick={() => navigate('/')}>
          <MdArrowBack />
          <span>Home</span>
        </button>
        <div className="ap__header-text">
          <h1 className="ap__title">Extracurricular Activities</h1>
          <p className="ap__subtitle">Click any card to see what happened in that activity</p>
        </div>
      </div>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="ap__filters">
        {CATEGORIES.map((cat, i) => (
          <button
            key={i}
            className={`ap__filter-btn ${activeFilter === cat ? 'active' : ''}`}
            onClick={() => handleActivityFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── Grid ─────────────────────────────────────────────────── */}
      <motion.div
        className="ap__grid"
        animate={animateCard}
        transition={{ duration: 0.4 }}
      >
        {filterActivity.map((activity, index) => (
          <motion.div
            className="ap__card"
            key={index}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.06 }}
            onClick={() => setSelectedActivity(activity)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedActivity(activity)}
          >
            {/* Image */}
            <div className="ap__card-img">
              <img src={images[activity.img]} alt={activity.title} loading="lazy" />
              <div className="ap__card-overlay">
                <span className="ap__card-read">Read Details →</span>
              </div>
            </div>

            {/* Body */}
            <div className="ap__card-body">
              <div className="ap__card-tag">{activity.tags && activity.tags[0]}</div>
              <h3 className="ap__card-title">{activity.title}</h3>
              <p className="ap__card-summary" style={{ fontStyle: 'italic', opacity: 0.8 }}>{activity.date}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* ── Modal ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {selectedActivity && (
          <motion.div
            className="ap__modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedActivity(null)}
          >
            <motion.div
              className="ap__modal"
              initial={{ opacity: 0, scale: 0.92, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 40 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="ap__modal-header">
                <img
                  className="ap__modal-hero"
                  src={images[selectedActivity.img]}
                  alt={selectedActivity.title}
                />
                <div className="ap__modal-header-overlay">
                  <span className="ap__modal-tag">{selectedActivity.tags && selectedActivity.tags[0]}</span>
                  <h2 className="ap__modal-title">{selectedActivity.title}</h2>
                  <p className="ap__modal-tech">{selectedActivity.date}</p>
                </div>
                <button className="ap__modal-close" onClick={() => setSelectedActivity(null)}>
                  <MdClose />
                </button>
              </div>

              {/* Activity Details */}
              <div className="ap__modal-body">
                {selectedActivity.description ? (
                  <motion.div
                    className="ap__story-section"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="ap__story-icon" style={{ color: '#f59e0b', borderColor: '#f59e0b33', background: '#f59e0b15' }}>
                      <HiLightBulb />
                    </div>
                    <div className="ap__story-content">
                      <h4 className="ap__story-label" style={{ color: '#f59e0b' }}>
                        What Happened
                      </h4>
                      <p className="ap__story-text">{selectedActivity.description}</p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    className="ap__story-section"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="ap__story-content">
                      <p className="ap__story-text" style={{ fontStyle: 'italic', opacity: 0.7 }}>
                        No additional details provided for this activity.
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AllActivities;
